import { AxisCode } from '@atlas/types'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { campaignPorts } from '@/adapters/campaign-composition'
import type { Campaign, CampaignStatus, CreateCampaignInput, UpdateCampaignInput } from '@/domain/campaign.types'
import {
	CAMPAIGN_LIST_MAX_LIMIT,
	CAMPAIGN_LIST_PAGE_SIZE,
	NON_ARCHIVED_CAMPAIGN_STATUSES,
} from '@/domain/campaign.types'
import { isCampaignError } from '@/domain/campaign-errors'
import type {
	CampaignBulkDownloadScope,
	CampaignListFilter,
	CampaignListPageRequest,
	CampaignUploadExecutionOptions,
	CampaignUploadInput,
} from '@/domain/campaign-ports'
import { narrowToApplicable } from '@/lib/axis-applicability'
import { deriveCampaignDownloadCapabilities } from '@/lib/campaign-download-capabilities'
import { CAMPAIGN_MESSAGES_KEY } from './useCampaignMessages'

// React Query hooks over the composed Campaign capability ports — asynchronous
// orchestration only (loading/pending/error state, cache-like behavior), never
// an HTTP client. Every hook obtains its port through `campaignPorts`; none
// imports a concrete in-memory implementation or singleton directly. Role→
// capability resolution is NOT among these hooks — it stays a direct pure-
// function call in a page or feature container.

const CAMPAIGNS_KEY = ['campaigns'] as const
const CAMPAIGNS_LIST_KEY = [...CAMPAIGNS_KEY, 'list'] as const
const campaignKey = (campaignId: string) => [...CAMPAIGNS_KEY, campaignId] as const
const campaignAvailableActionsKey = (campaignId: string) => [...campaignKey(campaignId), 'available-actions'] as const

/** Shared query definition for one Campaign list page — reused by `useCampaigns` (one page) and `useCampaignsPages` (many, via `useQueries`). */
function campaignsListQuery(filter: CampaignListFilter, page?: CampaignListPageRequest) {
	return {
		queryKey: [...CAMPAIGNS_LIST_KEY, filter, page] as const,
		queryFn: () => campaignPorts.query.listCampaigns(filter, page),
	}
}

/**
 * One Campaign list page. `enabled` lets a caller declare a page it only
 * sometimes needs (e.g. a fallback lookup) without issuing the request on every
 * render — it defaults to always-on, so existing callers are unaffected.
 */
export function useCampaigns(
	filter: CampaignListFilter,
	page?: CampaignListPageRequest,
	options?: { enabled?: boolean },
) {
	return useQuery({ ...campaignsListQuery(filter, page), enabled: options?.enabled ?? true })
}

/**
 * One independent query per page request, so page N can still be loading while
 * pages 1..N-1 are already resolved. `enabled` lets a caller declare pages it
 * only sometimes needs — a picker whose axis is filtered out should not pay for
 * the list. Defaults to always-on, matching {@link useCampaigns}.
 */
export function useCampaignsPages(
	filter: CampaignListFilter,
	pages: readonly CampaignListPageRequest[],
	options?: { enabled?: boolean },
) {
	return useQueries({
		queries: pages.map((page) => ({ ...campaignsListQuery(filter, page), enabled: options?.enabled ?? true })),
	})
}

/**
 * One scope's campaigns: those classified to ONE value of ONE axis, non-archived, first
 * page only. Keyed on the axis and the code alone, so every surface asking the same
 * question shares one cache entry — a brand page and any product page under that brand
 * issue the brand read once between them.
 */
function scopedCampaignsQuery(axisCode: string, code: string | null) {
	return {
		queryKey: [...CAMPAIGNS_LIST_KEY, 'applicable', axisCode, code] as const,
		queryFn: () =>
			campaignPorts.query.listCampaigns(
				{ status: NON_ARCHIVED_CAMPAIGN_STATUSES, classification: { [axisCode]: [code as string] } },
				{ page: 1, limit: CAMPAIGN_LIST_PAGE_SIZE },
			),
		enabled: code !== null,
	}
}

/** First occurrence wins, so the product leg's copy of a campaign both legs return is the one kept. */
function dedupeByCampaignId(campaigns: readonly Campaign[]): Campaign[] {
	const seen = new Set<string>()
	const unique: Campaign[] = []
	for (const campaign of campaigns) {
		if (seen.has(campaign.campaignId)) continue
		seen.add(campaign.campaignId)
		unique.push(campaign)
	}
	return unique
}

/**
 * The campaigns that APPLY to a product, or to a brand, as a consultation surface asks it.
 *
 * "Applies" is an OR across two axes — classified to THIS product, or to its BRAND with no
 * product of its own (a brand-wide campaign runs on every product under it, as a worldwide
 * asset applies in every market). `filters[...]` combines with AND across axes, so the OR
 * is two reads unioned rather than one request. Each is its own query, so each caches and
 * fails on its own and a surface with only one scope issues only that one.
 *
 * The union is then narrowed on the campaigns' OWN returned tags rather than in either
 * request's filters, because a classification filter matches a value's DESCENDANTS: asking
 * the server for the resolved market would drop a campaign classified to the region above
 * it, which is the opposite of applicability. The caller states the narrowing — it is the
 * page's question, not this hook's.
 *
 * Failure is reported separately from emptiness: "no campaigns apply" is an answer a reader
 * acts on, and returning it for a read that never landed would clear a product of campaigns
 * the service was simply unable to state.
 */
export function useApplicableCampaigns(
	productCode: string | null,
	brandCode: string | null,
	narrowing: Record<string, string[]> = {},
) {
	const legs = useQueries({
		queries: [scopedCampaignsQuery(AxisCode.BRAND, productCode), scopedCampaignsQuery(AxisCode.BRAND, brandCode)],
	})
	// Only the legs that were actually asked: a disabled query never resolves, so counting
	// it would leave the section loading forever on a product with no brand.
	const asked = legs.filter((_, index) => [productCode, brandCode][index] !== null)
	const union = dedupeByCampaignId(asked.flatMap((leg) => leg.data ?? []))

	return {
		// Wrapped into the `Tagged` shape the shared helper takes, then unwrapped — one
		// applicability implementation for campaigns, files and messages alike.
		campaigns: narrowToApplicable(
			union.map((campaign) => ({ campaign, tags: campaign.classification.tags })),
			narrowing,
		).map((entry) => entry.campaign),
		isLoading: asked.some((leg) => leg.isLoading),
		isError: asked.some((leg) => leg.isError),
	}
}

/**
 * The name of the campaign a work order carries by code, for a row that should read like its
 * neighbours. Falls back to the code until (or unless) it resolves, so the row never vanishes
 * while the list loads. Reads the first list page at its largest size, the page every work-order
 * panel shares from the cache.
 */
export function useCampaignName(campaignCode: string | null): string | null {
	const { data: campaigns = [] } = useCampaigns(
		{},
		{ page: 1, limit: CAMPAIGN_LIST_MAX_LIMIT },
		{ enabled: campaignCode !== null },
	)
	if (!campaignCode) return null
	return campaigns.find((campaign) => campaign.campaignCode === campaignCode)?.name ?? campaignCode
}

export function useCampaign(campaignId: string) {
	return useQuery({
		queryKey: campaignKey(campaignId),
		queryFn: () => campaignPorts.query.getCampaign(campaignId),
		enabled: campaignId.length > 0,
	})
}

export function useCreateCampaign() {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (input: CreateCampaignInput) => campaignPorts.command.createCampaign(input),
		onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_LIST_KEY }),
	})
}

export function useUpdateCampaign() {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ campaignId, input }: { campaignId: string; input: UpdateCampaignInput }) =>
			campaignPorts.command.updateCampaign(campaignId, input),
		// Detail AND list — an edited name/classification must not render stale in the list row.
		onSuccess: (_data, { campaignId }) =>
			Promise.all([
				qc.invalidateQueries({ queryKey: campaignKey(campaignId) }),
				qc.invalidateQueries({ queryKey: CAMPAIGNS_LIST_KEY }),
			]),
	})
}

export function useDeleteCampaign() {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (campaignId: string) => campaignPorts.command.deleteCampaign(campaignId),
		onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_LIST_KEY }),
	})
}

export function useCampaignAvailableActions(campaignId: string) {
	return useQuery({
		queryKey: campaignAvailableActionsKey(campaignId),
		queryFn: () => campaignPorts.lifecycle.getAvailableActions(campaignId),
		enabled: campaignId.length > 0,
	})
}

export function useTransitionCampaign() {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ campaignId, to }: { campaignId: string; to: CampaignStatus }) =>
			campaignPorts.lifecycle.transition(campaignId, to),
		// Success: detail, available-actions, AND the list — a new status must not render stale anywhere.
		onSuccess: (_data, { campaignId }) =>
			Promise.all([
				qc.invalidateQueries({ queryKey: campaignKey(campaignId) }),
				qc.invalidateQueries({ queryKey: campaignAvailableActionsKey(campaignId) }),
				qc.invalidateQueries({ queryKey: CAMPAIGNS_LIST_KEY }),
			]),
		// A 409 conflict refreshes detail + available-actions (so no stale action stays offered)
		// but never re-submits the transition itself — the existing conflict view state stands.
		onError: (error, { campaignId }) => {
			if (isCampaignError(error) && error.category === 'conflict') {
				return Promise.all([
					qc.invalidateQueries({ queryKey: campaignKey(campaignId) }),
					qc.invalidateQueries({ queryKey: campaignAvailableActionsKey(campaignId) }),
				])
			}
			return undefined
		},
	})
}

export function useCampaignFiles(campaignId: string) {
	return useQuery({
		queryKey: [...campaignKey(campaignId), 'files'],
		queryFn: () => campaignPorts.files.listAssociatedFiles(campaignId),
		enabled: campaignId.length > 0,
	})
}

const CAMPAIGN_FILE_DISCOVERY_KEY = [...CAMPAIGNS_KEY, 'file-discovery'] as const

export function useCampaignFileDiscovery() {
	return useQuery({
		queryKey: CAMPAIGN_FILE_DISCOVERY_KEY,
		queryFn: () => campaignPorts.discovery.discoverFiles(),
	})
}

export function useAssociateCampaignFiles(campaignId: string) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (fileIds: readonly string[]) => campaignPorts.association.associateFiles(campaignId, fileIds),
		onSuccess: () =>
			Promise.all([
				qc.invalidateQueries({ queryKey: [...campaignKey(campaignId), 'files'] }),
				qc.invalidateQueries({ queryKey: CAMPAIGN_FILE_DISCOVERY_KEY }),
			]),
	})
}

/**
 * Associates Messages with a Campaign, mirroring `useAssociateCampaignFiles`
 * exactly. Takes BOTH the Campaign's id (the association endpoint's path
 * param) and its immutable code (`useCampaignMessages`'s read is keyed by
 * code, not id, matching `useWorkOrderMessages`'s `order_code`-keyed read) —
 * invalidates the read by code on success.
 */
export function useAssociateCampaignMessages(campaignId: string, campaignCode: string) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (messageIds: readonly string[]) =>
			campaignPorts.messageAssociation.associateMessages(campaignId, messageIds),
		onSuccess: () => qc.invalidateQueries({ queryKey: [CAMPAIGN_MESSAGES_KEY, campaignCode] }),
	})
}

/**
 * The associated-Files and discovery keys, invalidated together after a File
 * becomes associated. Never the Campaign detail key: no persisted Campaign field
 * changes as a result of an upload or association.
 */
function invalidateAfterAssociation(qc: ReturnType<typeof useQueryClient>, campaignId: string) {
	return Promise.all([
		qc.invalidateQueries({ queryKey: [...campaignKey(campaignId), 'files'] }),
		qc.invalidateQueries({ queryKey: CAMPAIGN_FILE_DISCOVERY_KEY }),
	])
}

export function useUploadAndAssociate(campaignId: string) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ file, options }: { file: CampaignUploadInput; options?: CampaignUploadExecutionOptions }) =>
			campaignPorts.upload.uploadAndAssociate(campaignId, file, options),
		// Only a complete five-step success invalidates. Every failure kind —
		// including `association-failed`, where the File exists but is not yet
		// associated — leaves both caches untouched.
		onSuccess: (outcome) => {
			if (outcome.kind === 'success') void invalidateAfterAssociation(qc, campaignId)
		},
	})
}

export function useRetryCampaignUploadAssociation(campaignId: string) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({
			operationToken,
			options,
		}: {
			operationToken: string
			options?: Pick<CampaignUploadExecutionOptions, 'onTransientState' | 'signal'>
		}) => campaignPorts.upload.retryAssociation(campaignId, operationToken, options),
		onSuccess: (outcome) => {
			if (outcome.kind === 'success') void invalidateAfterAssociation(qc, campaignId)
		},
	})
}

/**
 * Download-capability state, derived from the Files this Campaign has already
 * fetched — no query key, no request, no port operation, because no backend
 * route exposes it. Its loading/error behavior is exactly `useCampaignFiles`'s.
 */
export function useCampaignDownloadCapabilities(campaignId: string) {
	const files = useCampaignFiles(campaignId)
	return deriveCampaignDownloadCapabilities(files.data ?? [])
}

export function useDownloadCampaignFile() {
	return useMutation({
		mutationFn: (fileId: string) => campaignPorts.download.downloadIndividual(fileId),
	})
}

/** One synchronous bulk-download request (selected or all) — a command, not cached data, so no query key. */
export function useDownloadCampaignBulk() {
	return useMutation({
		mutationFn: ({ campaignId, scope }: { campaignId: string; scope: CampaignBulkDownloadScope }) =>
			campaignPorts.download.downloadBulk(campaignId, scope),
	})
}

export function useCampaignClassificationCatalog() {
	return useQuery({
		queryKey: ['campaign-classification-catalog'],
		queryFn: () => campaignPorts.classificationCatalog.getCatalog(),
		staleTime: 5 * 60 * 1000,
	})
}
